"use client";

import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { Card, CardContent } from "@/components/ui/card";
import { MatchFilterOptions } from "@/components/landing/match-filter-options";
import { routes } from "@/routes";
import { PreviewCard } from "@/components/landing/preview-card";
import { MiniProfileMMR } from "@/components/landing/mini-profile-mmr";
import { SkeletonLeaderboard } from "@/components/landing/skeleton-leaderboard";
import {
  MapPin,
  Trophy,
  TrendingUp,
  MessageSquare,
  Camera,
  Lock,
  Users,
} from "lucide-react";
import { FeatureShowcase, TierCard } from "@/components/ui-library";

export const FeaturesDeepDiveSection = () => {
  return (
    <section id="features-deep-dive" className="w-full py-12 md:py-24 lg:py-32">
      <Container>
        <h2 className="text-3xl font-bold tracking-tight text-center sm:text-4xl mb-4 px-4 md:px-0">
          Everything You Need to Play More Golf
        </h2>
        <p className="text-center text-muted-foreground mb-12 md:mb-16 max-w-2xl mx-auto">
          From discovering courses to challenging players to sharing your best
          moments
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-12">
          {/* Feature 1: Browse Courses (Free) */}
          <FeatureShowcase
            variant="default"
            badge="Free"
            icon={MapPin}
            title="Browse Courses"
            description="Explore golf courses near you. View detailed course information, hours, contact details, and read reviews from other golfers. Find your next favorite course without paying a dime."
          >
            <div className="w-full mb-3">
              <Link href={routes.dashboard.courses.root}>
                <Button
                  variant="ghost"
                  className="w-full border border-dashed border-primary/50 text-primary hover:bg-primary/5 hover:text-primary"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Searching Near You...
                </Button>
              </Link>
            </div>
            <div className="space-y-2">
              <PreviewCard
                title="Willow Creek Golf Club"
                category="Public Course • 18 Holes"
              />
              <PreviewCard
                title="Sunset Valley Links"
                category="Public Course • 27 Holes"
              />
            </div>
          </FeatureShowcase>

          <FeatureShowcase
            variant="emerald"
            badge="Member"
            icon={Trophy}
            title="Challenge & Connect"
            description="Send challenges to other golfers, message your connections, and join the social feed. Post your rounds, tag courses, and become part of the community."
            featured
          >
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-1 text-emerald-500">
                <Trophy className="h-4 w-4" />
                <span>5 challenges/week</span>
              </div>
              <div className="flex items-center gap-1 text-emerald-500">
                <Camera className="h-4 w-4" />
                <span>10 posts/week</span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-1 text-emerald-500">
                <MessageSquare className="h-4 w-4" />
                <span>Messaging</span>
              </div>
              <div className="flex items-center gap-1 text-emerald-500">
                <Users className="h-4 w-4" />
                <span>Unlimited leagues</span>
              </div>
            </div>
            <MatchFilterOptions />
          </FeatureShowcase>
        </div>

        {/* Key Features Highlight */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-center mb-8">
            Powered by Innovation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* AI Scorecard */}
            <Card className="bg-card/50 border-emerald-500/20">
              <CardContent className="pt-6">
                <Camera className="h-8 w-8 text-emerald-500 mb-3" />
                <h4 className="font-semibold mb-2">AI Scorecard OCR</h4>
                <p className="text-sm text-muted-foreground">
                  Instant digitization with Gemini 2.0 Flash. Just snap a photo.
                </p>
              </CardContent>
            </Card>

            {/* Weather */}
            <Card className="bg-card/50 border-blue-500/20">
              <CardContent className="pt-6">
                <MapPin className="h-8 w-8 text-blue-500 mb-3" />
                <h4 className="font-semibold mb-2">Real-Time Weather</h4>
                <p className="text-sm text-muted-foreground">
                  Course conditions at your fingertips, updated live.
                </p>
              </CardContent>
            </Card>

            {/* Social Posts */}
            <Card className="bg-card/50 border-purple-500/20">
              <CardContent className="pt-6">
                <Users className="h-8 w-8 text-purple-500 mb-3" />
                <h4 className="font-semibold mb-2">Rich Media Posts</h4>
                <p className="text-sm text-muted-foreground">
                  Share rounds with images, videos, and audience targeting.
                </p>
              </CardContent>
            </Card>

            {/* Lobbies */}
            <Card className="bg-card/50 border-amber-500/20">
              <CardContent className="pt-6">
                <Trophy className="h-8 w-8 text-amber-500 mb-3" />
                <h4 className="font-semibold mb-2">Live Lobbies</h4>
                <p className="text-sm text-muted-foreground">
                  Real-time matchmaking. Find partners instantly.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* What's Locked for Free Users */}
        <div className="mt-16 p-6 rounded-lg bg-muted/50 border border-border/50">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold">
              Free users can browse courses, but...
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm text-muted-foreground">
            <div className="flex flex-col items-center gap-1">
              <Trophy className="h-5 w-5 text-red-400" />
              <span>No challenges</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <MessageSquare className="h-5 w-5 text-red-400" />
              <span>No messaging</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Camera className="h-5 w-5 text-red-400" />
              <span>No posting</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Users className="h-5 w-5 text-red-400" />
              <span>No social feed</span>
            </div>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-4">
            Upgrade to{" "}
            <span className="text-emerald-500 font-medium">
              Member ($8.99/mo)
            </span>{" "}
            to unlock all social features
          </p>
        </div>
      </Container>
    </section>
  );
};
